import React from 'react';
import styled from 'styled-components';
import useLocale from '../hooks/useLocale';

const ToggleWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const TogglePill = styled.div`
  position: relative;
  width: 86px;
  height: 36px;
  background: ${({ theme }) => theme.colors.white};
  border-radius: 9999px;
  border: 1px solid ${({ theme }) => theme.colors.lightGray}30;
  display: flex;
  align-items: center;
  padding: 4px;
  gap: 4px;
  box-shadow: 0 6px 18px rgba(2,6,23,0.04);
`;

const ToggleOption = styled.button`
  flex: 1 1 50%;
  height: 100%;
  background: ${({ $active, $scrolled, theme }) => ($active ? ($scrolled ? theme.colors.primary : theme.colors.white) : 'transparent')};
  color: ${({ $active, $scrolled, theme }) => ($active ? ($scrolled ? theme.colors.white : theme.colors.primary) : theme.colors.dark)};
  border: none;
  font-weight: ${({ $active, theme }) => ($active ? theme.fontWeights.semibold : theme.fontWeights.medium)};
  border-radius: 9999px;
  cursor: pointer;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 180ms ease, color 180ms ease, transform 120ms ease;
  text-transform: uppercase;
  font-size: 0.78rem;
  gap: 6px;
  padding: 0 6px;

  &:hover {
    transform: translateY(-1px);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}20;
    border-radius: 9999px;
  }
`;

const FlagSpan = styled.span`
  font-size: 1rem;
  line-height: 1;
`;

const LanguageToggle = ({ scrolled = false }) => {
  const { locale, setLocale, t } = useLocale();

  const handleToggle = (lng) => {
    if (lng !== locale) setLocale(lng);
  };

  // Short codes fixed to EN/PT for compact UI
  const enShort = 'EN';
  const ptShort = 'PT';

  // localized full names for aria labels
  const enLabel = (t && t('languageNames.en')) || 'English';
  const ptLabel = (t && t('languageNames.pt')) || 'Português';

  return (
    <ToggleWrapper role="group" aria-label="Language toggle">
      <TogglePill>
        <ToggleOption
          onClick={() => handleToggle('en')}
          $active={locale === 'en'}
          $scrolled={scrolled}
          aria-pressed={locale === 'en'}
          aria-label={`Switch to ${enLabel}`}
          title={enLabel}
        >
          <FlagSpan role="img" aria-hidden="true">🇬🇧</FlagSpan>
          <span>{enShort}</span>
        </ToggleOption>

        <ToggleOption
          onClick={() => handleToggle('pt')}
          $active={locale === 'pt'}
          $scrolled={scrolled}
          aria-pressed={locale === 'pt'}
          aria-label={`Switch to ${ptLabel}`}
          title={ptLabel}
        >
          <FlagSpan role="img" aria-hidden="true">🇧🇷</FlagSpan>
          <span>{ptShort}</span>
        </ToggleOption>
      </TogglePill>
    </ToggleWrapper>
  );
};

export default LanguageToggle;
